import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X, Home, Calendar, Users, User, LogOut,
  ChevronRight, Bell, Settings, HelpCircle, UsersRound, Shield,
  Sun, Moon, Activity, Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthProvider';
import { isAdmin } from '../utils/adminUtils';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  theme?: 'light' | 'dark' | 'system';
  onToggleTheme?: () => void;
  apiCallCount?: number;
  onToggleApiTracker?: () => void;
  pendingInvitations?: number;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  theme = 'light',
  onToggleTheme,
  apiCallCount = 0,
  onToggleApiTracker,
  pendingInvitations = 0
}) => {
  const { user, signOut } = useAuth();
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLogout = async () => {
    await signOut();
    onClose();
  };

  const menuItems = [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/meetups', label: 'Meetups', icon: Calendar },
    { to: '/friends', label: 'Friends', icon: Users },
    { to: '/groups', label: 'Groups', icon: UsersRound },
    ...(isAdmin(user) ? [{ to: '/admin', label: 'Admin', icon: Shield }] : []),
  ];

  const secondaryItems = [
    { to: '/notifications', label: 'Notifications', icon: Bell, badge: 3 },
    { to: '/settings', label: 'Settings', icon: Settings },
    { to: '/help', label: 'Help & Support', icon: HelpCircle },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 md:hidden
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        role="presentation"
      />

      {/* Menu Panel */}
      <div
        className={`
          fixed top-0 right-0 h-full w-80 bg-white dark:bg-gray-900 z-[70] shadow-xl dark:shadow-2xl
          flex flex-col overflow-hidden
          transform transition-transform duration-300 ease-out md:hidden
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="bg-gray-800 dark:bg-gray-950 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Menu</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {user && (
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary dark:bg-blue-600 rounded-full flex items-center justify-center text-lg font-bold text-white">
                {user.email?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-semibold">{user.email}</p>
                <p className="text-sm text-gray-300">User</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
          {/* Main Navigation */}
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Main Menu
            </h3>
            <div className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.to;
                
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`
                      flex items-center justify-between px-4 py-3 rounded-lg
                      transition-all duration-200 group min-h-[44px]
                      ${isActive 
                        ? 'bg-gray-100 dark:bg-gray-800 text-primary dark:text-blue-400' 
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${isActive ? 'text-primary dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* Secondary Navigation */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              More
            </h3>
            <div className="space-y-1">
              {/* Theme Toggle */}
              {onToggleTheme && (
                <button
                  onClick={() => {
                    onToggleTheme();
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group min-h-[44px]"
                >
                  <div className="flex items-center gap-3">
                    {theme === 'dark' ? (
                      <Sun className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    )}
                    <span className="font-medium">
                      {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
                </button>
              )}

              {/* Invitations */}
              <Link
                to="/invitations"
                className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group min-h-[44px]"
              >
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  <span className="font-medium">Invitations</span>
                </div>
                <div className="flex items-center gap-2">
                  {pendingInvitations > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingInvitations}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>

              {/* API Monitor - Admin Only */}
              {isAdmin(user) && onToggleApiTracker && (
                <button
                  onClick={() => {
                    onToggleApiTracker();
                    onClose();
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group min-h-[44px]"
                >
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span className="font-medium">API Monitor</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {apiCallCount > 0 && (
                      <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {apiCallCount > 99 ? '99+' : apiCallCount}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              )}

              {secondaryItems.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group min-h-[44px]"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.badge && (
                        <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* User Actions */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <Link
              to="/profile"
              className="flex items-center justify-between px-4 py-3 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 group mb-2 min-h-[44px]"
            >
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="font-medium">My Profile</span>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group min-h-[44px]"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </div>
              <ChevronRight className="w-4 h-4 text-red-400 dark:text-red-500 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </nav>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950">
          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <p>Vitly v1.0.0</p>
            <p className="mt-1">© 2025 All rights reserved</p>
          </div>
        </div>
      </div>
    </>
  );
};